import React, { useReducer, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useRouteMatch } from "react-router-dom";

import {
  getMembersList,
  getProjectDetails,
  updateProject,
  updateTasksList,
  getTaskDetails,
  getTasksCounters,
  getSingleTaskForList,
  getSingleTaskForMyTaskList,
  getMyTasks,
  getActivityList,
  getFilteredProjectsList,
  getDictionaryListElement,
  getDictionaryList,
  getDictionaryDetails,
  getWsContentProposalElementsSummary,
  getProjectTasksAutocomplete,
  getSingleWorkspaceTaskForList,
  deleteWorkspaceTask,
  getProjectAudienceOverlap,
  setIsGeneratingSuggestedFilters,
} from "./redux";
import { showToast } from "./app/methods/showToast";
import {
  sendNotification,
  setPdfReadyModalData,
  setWebSocket,
} from "./redux/main/mainActions";
import { getDetectedPublications } from "./redux/automation/automationActions";
import { useInterval } from "./app/methods/useInterval";
import IDHFormattedMessage from "./app/components/IDHFormattedMessage/IDHFormattedMessage";
import {
  getCouponGroupDetails,
  getCouponGroups,
  getCouponsList,
} from "./redux/coupon/couponActions";
import { useQuery } from "./app/methods/useQuery";
import {
  customEqual,
  isStringUuid,
  isValueTaskType,
  urlIncludes,
} from "./utils/methods";
import {
  getGlobalTasks,
  getSingleGlobalTaskFromList,
  getGlobalTaskDetails,
} from "./redux/creator-database/creatorDatabaseActions";
import dictionaryFiltersReducer, {
  dictionaryFiltersReducerInitialState,
} from "./app/Dictionary/redux/dictionaryFiltersReducer";
import { RootState } from "./redux/reducers";
import creatorDatabaseFiltersReducer, {
  creatorDatabaseFiltersReducerInitialState,
} from "./app/CreatorDatabase/redux/creatorDatabaseFiltersReducer";
import XssFilter from "./app/components/XssFilter/XssFilter";

interface WebSocketData {
  actionTaken: string;
  actionToTake: string;
  objectUuid: string;
  wsProjectUuid?: string;
  wsWorkspaceUuid: string;
  messageType?: string;
  messageHeader?: string;
  messageText?: string;
  disableMessageAutoClose?: boolean;
  displayMessageTextAsRawHtml?: boolean;
  wsTaskUuids?: string[];
  dispatchType?: string;
  payload?: any;
}

const ACTIONS_TO_TAKE = {
  ACTION_UPDATE_TASK: "UPDATE_TASK",
  ACTION_REMOVE_TASK: "REMOVE_TASK",
  ACTION_UPDATE_TASK_COMMENTS: "UPDATE_TASK_COMMENTS",
  ACTION_UPDATE_PUBLISHED_CONTENTS: "UPDATE_PUBLISHED_CONTENTS",
  ACTION_UPDATE_CONTENT_PROPOSAL_ELEMENTS_SUMMARY:
    "UPDATE_CONTENT_PROPOSAL_ELEMENTS_SUMMARY",
  ACTION_UPDATE_PROJECTS_LIST: "UPDATE_PROJECTS_LIST",
  ACTION_UPDATE_PROJECT_TASKS_LIST: "UPDATE_PROJECT_TASKS_LIST",
  ACTION_UPDATE_PROJECT_TASKS_LIST_ELEMENT: "UPDATE_PROJECT_TASKS_LIST_ELEMENT",
  ACTION_UPDATE_MY_TASKS_LIST: "UPDATE_MY_TASKS_LIST",
  ACTION_UPDATE_MY_TASKS_LIST_ELEMENT: "UPDATE_MY_TASKS_LIST_ELEMENT",
  ACTION_UPDATE_PROJECT: "UPDATE_PROJECT",
  ACTION_UPDATE_ACTIVITY_LIST: "UPDATE_ACTIVITY_LIST",
  ACTION_UPDATE_WORKSPACE_MEMBERS: "UPDATE_WORKSPACE_MEMBERS",
  ACTION_UPDATE_COUPON_GROUP_LIST: "UPDATE_COUPON_GROUP_LIST",
  ACTION_UPDATE_COUPON_LIST: "UPDATE_COUPON_LIST",
  ACTION_UPDATE_GLOBAL_TASK_LIST: "UPDATE_GLOBAL_TASK_LIST",
  ACTION_UPDATE_GLOBAL_TASK_DETAILS: "UPDATE_GLOBAL_TASK_DETAILS",
  ACTION_UPDATE_GLOBAL_TASK: "UPDATE_GLOBAL_TASK",
  ACTION_SHOW_RELOAD_APP_MODAL: "SHOW_RELOAD_APP_MODAL",
  ACTION_SHOW_NOTIFICATION_TOAST: "SHOW_NOTIFICATION_TOAST",
  ACTION_SHOW_REPORT_IS_READY_MODAL: "SHOW_REPORT_IS_READY_MODAL",
  ACTION_SHOW_PUBLICATION_DETECTION_MODAL: "SHOW_PUBLICATION_DETECTION_MODAL",
  ACTION_UPDATE_DICTIONARY_ELEMENT: "UPDATE_DICTIONARY_ELEMENT",
  ACTION_UPDATE_DICTIONARY_ELEMENTS_LIST: "UPDATE_DICTIONARY_ELEMENTS_LIST",
  ACTION_UPDATE_DICTIONARY_ELEMENT_LIST_ELEMENT:
    "UPDATE_DICTIONARY_ELEMENT_LIST_ELEMENT",
  TRIGGER_DISPATCH_FRONT_ACTION: "TRIGGER_DISPATCH_FRONT_ACTION",
  ACTION_UPDATE_PROJECT_AUDIENCE_OVERLAP: "UPDATE_PROJECT_AUDIENCE_OVERLAP",
} as const;

const ACTIONS_TAKEN = {
  PROJECT_FILTERS_UPDATED: "PROJECT_FILTERS_UPDATED",
  PROJECT_META_VALUE_SET: "PROJECT_META_VALUE_SET",
  SUGGESTED_PROJECT_FILTERS_UPDATED: "SUGGESTED_PROJECT_FILTERS_UPDATED",
} as const;

const DISPATCH_TYPE = {
  PDF_READY: "PDF_READY",
} as const;

const WEBSOCKET_PROCESS_INTERVAL = 3000;

const getInitialWebSocketMessageQueue = () => {
  return Object.values(ACTIONS_TO_TAKE).reduce(
    (acc, actionName) => ({
      ...acc,
      [actionName]: [],
    }),
    {},
  );
};

function WebsocketListener() {
  const params = useParams<{
    projectId: string;
    taskId: string;
    couponGroupId: string;
    dictionaryUuid: string;
  }>();
  const dispatch = useDispatch();
  const query = useQuery();
  const { url } = useRouteMatch();

  const onMyTasks = urlIncludes("/my-actions");
  const onSummary = urlIncludes("/summary");
  const onCreatorDatabase = urlIncludes("/creator-database");
  const onDictionary = urlIncludes("/dict");

  const {
    activityReducer: { filters: activityFilters, offset, limit },
    projectFiltersReducer: { filters: projectFilters, sortBy, sort },
    mainReducer: { websocket, identity, activeWorkspaceUuid },
    projectReducer: { taskType, projectsListOffset },
    creatorDatabaseReducer: { globalTaskDetails, globalTaskColumns },
    taskReducer: { tableInputFocused },
  } = useSelector((state: RootState) => state);

  const dictionaryList = useSelector(
    (state: RootState) => state.dictionaryReducer.dictionaryList,
    customEqual,
  );

  const [dictionaryFilters] = useReducer(
    dictionaryFiltersReducer,
    dictionaryFiltersReducerInitialState(dictionaryList.columns),
  );

  const [creatorDatabaseFilters] = useReducer(
    creatorDatabaseFiltersReducer,
    creatorDatabaseFiltersReducerInitialState(globalTaskColumns),
  );

  const {
    filters: filtersCreatorDatabase,
    sortBy: sortByCreatorDatabase,
    sort: sortCreatorDatabase,
  } = creatorDatabaseFilters;

  const [tabFocused, setTabFocused] = useState(true);
  const [webSocketMessageQueue, setWebSocketMessageQueue] = useState<{
    [actionToTake: string]: WebSocketData[];
  }>(getInitialWebSocketMessageQueue);

  const isWebSocketActionAlreadyEnqueued = (data: WebSocketData): boolean => {
    for (const enqueuedWs of webSocketMessageQueue[data.actionToTake]) {
      if (
        data.wsWorkspaceUuid === enqueuedWs.wsWorkspaceUuid &&
        data.actionToTake === enqueuedWs.actionToTake &&
        data.objectUuid === enqueuedWs.objectUuid
      ) {
        return true;
      }
    }

    return false;
  };

  const handleWebSocketAction = async (data: WebSocketData): Promise<void> => {
    removeMessageFromQueue(data);

    switch (data.actionToTake) {
      case ACTIONS_TO_TAKE.ACTION_UPDATE_TASK:
      case ACTIONS_TO_TAKE.ACTION_UPDATE_PUBLISHED_CONTENTS:
      case ACTIONS_TO_TAKE.ACTION_UPDATE_TASK_COMMENTS:
        if (data.objectUuid === params.taskId) {
          await dispatch(getTaskDetails(data.objectUuid));
        }

        break;
      case ACTIONS_TO_TAKE.ACTION_UPDATE_CONTENT_PROPOSAL_ELEMENTS_SUMMARY:
        if (data.objectUuid === params.taskId) {
          await dispatch(getWsContentProposalElementsSummary(data.objectUuid));
        }

        break;
      case ACTIONS_TO_TAKE.ACTION_UPDATE_PROJECTS_LIST:
        if (data.objectUuid === activeWorkspaceUuid) {
          if (url.endsWith("/projects")) {
            await dispatch(
              getFilteredProjectsList(activeWorkspaceUuid, {
                ...(projectFilters && {
                  filters: JSON.stringify(projectFilters),
                }),
                ...(sortBy && { sortBy }),
                ...(sort && { sort }),
                offset: 0,
                limit: projectsListOffset,
              }),
            );
          }
        }

        break;
      case ACTIONS_TO_TAKE.ACTION_REMOVE_TASK: {
        if (activeWorkspaceUuid === data.wsWorkspaceUuid) {
          const taskTypeOrNull = query.get("taskType");
          if (isValueTaskType(taskTypeOrNull)) {
            dispatch(deleteWorkspaceTask(data.objectUuid, taskTypeOrNull));
          }
          if (!onSummary && typeof params.projectId === "string") {
            dispatch(getTasksCounters(params.projectId));
          }
        }
        break;
      }
      case ACTIONS_TO_TAKE.ACTION_UPDATE_PROJECT_TASKS_LIST: {
        if (
          typeof params.projectId === "string" &&
          data.objectUuid === params.projectId
        ) {
          await dispatch(updateTasksList(params.projectId, taskType));
          await dispatch(getTasksCounters(params.projectId));
        }
        if (activeWorkspaceUuid === data.wsWorkspaceUuid) {
          dispatch(getProjectTasksAutocomplete(data.objectUuid));
        }
        break;
      }

      case ACTIONS_TO_TAKE.ACTION_UPDATE_PROJECT_TASKS_LIST_ELEMENT:
        if (data.wsProjectUuid === params.projectId) {
          await dispatch(getSingleTaskForList(data.objectUuid));
        }

        if (data.wsWorkspaceUuid === activeWorkspaceUuid && onSummary) {
          const taskTypeOrNull = query.get("taskType");
          if (isValueTaskType(taskTypeOrNull)) {
            dispatch(
              getSingleWorkspaceTaskForList(data.objectUuid, taskTypeOrNull),
            );
          }
        }

        break;

      case ACTIONS_TO_TAKE.ACTION_UPDATE_MY_TASKS_LIST:
        if (onMyTasks) {
          await dispatch(getMyTasks(activeWorkspaceUuid));
        }

        break;

      case ACTIONS_TO_TAKE.ACTION_UPDATE_MY_TASKS_LIST_ELEMENT:
        if (data.wsWorkspaceUuid === activeWorkspaceUuid && onMyTasks) {
          await dispatch(getSingleTaskForMyTaskList(data.objectUuid));
        }

        break;

      case ACTIONS_TO_TAKE.ACTION_UPDATE_PROJECT:
        if (data.objectUuid !== params.projectId) break;

        if (url.includes("/details")) {
          await dispatch(getProjectDetails(params.projectId));
        }

        const isCreatorSearchTab = query.get("tab") === "creatorSearch";
        const isProjectFiltersUpdated =
          data.actionTaken === ACTIONS_TAKEN.PROJECT_FILTERS_UPDATED;
        const areSuggestedProjectFiltersUpdated =
          data.actionTaken === ACTIONS_TAKEN.SUGGESTED_PROJECT_FILTERS_UPDATED;
        const isProjectMetaValueSet =
          data.actionTaken === ACTIONS_TAKEN.PROJECT_META_VALUE_SET;

        if (isCreatorSearchTab) {
          if (isProjectMetaValueSet) break;
          if (isProjectFiltersUpdated) {
            dispatch(setIsGeneratingSuggestedFilters(true));
          }
          if (areSuggestedProjectFiltersUpdated) {
            dispatch(setIsGeneratingSuggestedFilters(false));
          }
        }

        await dispatch(updateProject(params.projectId));
        break;

      case ACTIONS_TO_TAKE.ACTION_UPDATE_PROJECT_AUDIENCE_OVERLAP:
        if (
          data.objectUuid === params.projectId &&
          window.location.search.includes("audience-overlap")
        ) {
          await dispatch(getProjectAudienceOverlap(params.projectId));
        }
        break;

      case ACTIONS_TO_TAKE.ACTION_UPDATE_ACTIVITY_LIST:
        if (window.location.pathname.includes("/activity")) {
          await dispatch(
            getActivityList(activeWorkspaceUuid, activityFilters, 0, limit),
          );
        }

        break;

      case ACTIONS_TO_TAKE.ACTION_UPDATE_COUPON_GROUP_LIST:
        await dispatch(getCouponGroups(activeWorkspaceUuid, true));

        break;

      case ACTIONS_TO_TAKE.ACTION_UPDATE_COUPON_LIST:
        await dispatch(getCouponsList(activeWorkspaceUuid));
        if (params.couponGroupId) {
          await dispatch(getCouponGroupDetails(params.couponGroupId));
        }

        break;

      case ACTIONS_TO_TAKE.ACTION_UPDATE_WORKSPACE_MEMBERS:
        await dispatch(getMembersList(activeWorkspaceUuid));

        break;

      case ACTIONS_TO_TAKE.ACTION_SHOW_NOTIFICATION_TOAST:
        {
          if (!data.messageHeader || !data.messageText) {
            break;
          }

          const renderMessageText = () => {
            if (!data.messageText) return "";

            if (data.displayMessageTextAsRawHtml) {
              const cleanHtml = XssFilter(
                data.messageText,
                ["p", "b", "u", "i", "ul", "ol", "li", "br"],
                ["style"],
              );

              return (
                <span
                  dangerouslySetInnerHTML={{
                    __html: cleanHtml,
                  }}
                />
              );
            }

            return (
              <IDHFormattedMessage
                id={data.messageText}
                defaultMessage={data.messageText}
              />
            );
          };

          switch (data.messageType) {
            case "error":
            case "success":
            case "info":
            case "warning":
              showToast(
                data.messageType,
                data.messageHeader ? (
                  <IDHFormattedMessage
                    id={data.messageHeader}
                    defaultMessage={data.messageHeader}
                  />
                ) : (
                  ""
                ),
                renderMessageText(),
                data.disableMessageAutoClose,
              );

              break;
            default:
              break;
          }
        }

        break;
      case ACTIONS_TO_TAKE.ACTION_SHOW_RELOAD_APP_MODAL:
        await dispatch(sendNotification({ type: "reload" }));

        break;
      case ACTIONS_TO_TAKE.ACTION_SHOW_PUBLICATION_DETECTION_MODAL:
        if (data?.wsTaskUuids && data?.wsTaskUuids?.length > 0) {
          await dispatch(getDetectedPublications(data.wsTaskUuids));
        }

        break;

      case ACTIONS_TO_TAKE.ACTION_UPDATE_GLOBAL_TASK:
        if (onCreatorDatabase) {
          dispatch(getSingleGlobalTaskFromList(data.objectUuid));
        }
        break;

      case ACTIONS_TO_TAKE.ACTION_UPDATE_GLOBAL_TASK_LIST:
        if (onCreatorDatabase) {
          dispatch(
            getGlobalTasks(activeWorkspaceUuid, {
              filters: filtersCreatorDatabase,
              sortBy: sortByCreatorDatabase,
              sort: sortCreatorDatabase,
            }),
          );
        }
        break;

      case ACTIONS_TO_TAKE.ACTION_UPDATE_GLOBAL_TASK_DETAILS:
        if (data.objectUuid === globalTaskDetails?.uuid) {
          dispatch(getGlobalTaskDetails(data.objectUuid, true));
        }
        break;
      case ACTIONS_TO_TAKE.ACTION_UPDATE_DICTIONARY_ELEMENT:
        {
          const dictionaryElementUuid =
            query.get("displayDictionaryPreview") || "";
          if (isStringUuid(dictionaryElementUuid)) {
            dispatch(
              getDictionaryDetails(
                params.dictionaryUuid,
                dictionaryElementUuid,
              ),
            );
          }
        }
        break;
      case ACTIONS_TO_TAKE.ACTION_UPDATE_DICTIONARY_ELEMENTS_LIST:
        if (onDictionary && data.objectUuid === params.dictionaryUuid) {
          dispatch(
            getDictionaryList(data.objectUuid, {
              ...(Object.keys(dictionaryFilters?.filters ?? {}).length > 0 && {
                filters: JSON.stringify(dictionaryFilters.filters),
              }),
              ...(dictionaryFilters?.sortBy && {
                sortBy: dictionaryFilters.sortBy,
              }),
              ...(dictionaryFilters?.sort && { sort: dictionaryFilters.sort }),
            }),
          );
        }
        break;
      case ACTIONS_TO_TAKE.ACTION_UPDATE_DICTIONARY_ELEMENT_LIST_ELEMENT:
        if (onDictionary) {
          await dispatch(
            getDictionaryListElement(params.dictionaryUuid, data.objectUuid),
          );
        }
        break;
      default:
        break;
    }

    switch (data.dispatchType) {
      case DISPATCH_TYPE.PDF_READY:
        dispatch(setPdfReadyModalData(data.payload));
        break;
      default:
        break;
    }
  };

  const removeMessageFromQueue = (data: WebSocketData) => {
    setWebSocketMessageQueue((enqueuedWebSockets) => ({
      ...enqueuedWebSockets,
      [data.actionToTake]: enqueuedWebSockets[data.actionToTake].filter(
        (webSocketMessage: WebSocketData) =>
          data.wsWorkspaceUuid !== webSocketMessage.wsWorkspaceUuid &&
          data.actionToTake !== webSocketMessage.actionToTake &&
          data.objectUuid !== webSocketMessage.objectUuid,
      ),
    }));
  };

  useEffect(() => {
    const checkTabFocused = () => {
      if (document.visibilityState === "visible") {
        setTabFocused(true);
      } else {
        setTabFocused(false);
      }
    };

    document.addEventListener("visibilitychange", checkTabFocused);

    return () => {
      document.removeEventListener("visibilitychange", checkTabFocused);
    };
  }, []);

  useEffect(() => {
    connectWebSocket();
  }, [websocket]);

  const connectWebSocket = () => {
    if (websocket) {
      websocket.onopen = () => {
        console.info("WebSocket connected");
        websocket.send(JSON.stringify({ messageType: "ping" }));
      };

      websocket.onclose = () => {
        console.info("WebSocket disconnected");
        reconnectWebSocket();
      };
    }
  };

  const reconnectWebSocket = () => {
    setTimeout(() => {
      dispatch(
        setWebSocket(
          activeWorkspaceUuid,
          identity.id,
          identity.websocketChallenge,
        ),
      );
      connectWebSocket();
    }, 10000);
  };

  useInterval(() => {
    handleEnqueuedWebSockets();
  }, WEBSOCKET_PROCESS_INTERVAL);

  const handleEnqueuedWebSockets = () => {
    if (!tabFocused || tableInputFocused) return;

    Object.keys(webSocketMessageQueue).forEach((taskType) => {
      webSocketMessageQueue[taskType].forEach((data) => {
        setWebSocketMessageQueue((enqueuedWebSockets) => ({
          ...enqueuedWebSockets,
          [data.actionToTake]: enqueuedWebSockets[data.actionToTake].filter(
            (webSocketMessage: WebSocketData) =>
              data.wsWorkspaceUuid !== webSocketMessage.wsWorkspaceUuid &&
              data.actionToTake !== webSocketMessage.actionToTake &&
              data.objectUuid !== webSocketMessage.objectUuid,
          ),
        }));

        handleWebSocketAction(data);
      });
    });
  };

  useEffect(() => {
    if (websocket) {
      websocket.onmessage = (event: MessageEvent): void => {
        const dataObject: WebSocketData = {
          ...JSON.parse(event.data),
        };

        if (!dataObject.wsWorkspaceUuid) return;

        if (!isWebSocketActionAlreadyEnqueued(dataObject)) {
          setWebSocketMessageQueue((enqueuedWebSockets) => ({
            ...enqueuedWebSockets,
            [dataObject.actionToTake]: [
              ...enqueuedWebSockets[dataObject.actionToTake],
              dataObject,
            ],
          }));
        }
      };
    }
  }, [
    websocket,
    taskType,
    activeWorkspaceUuid,
    activityFilters.campaigns,
    activityFilters.members,
    activityFilters.dateRange,
    params.projectId,
    params.taskId,
    params.dictionaryUuid,
    params.couponGroupId,
    webSocketMessageQueue,
    offset,
    limit,
    query,
  ]);

  return null;
}

export default WebsocketListener;
